import axios, { AxiosError } from 'axios';

import LocalDataService from '@/service/LocalDataServices';
import { when } from '@/service/Switch';
import Swal from 'sweetalert2';
import querystring from 'query-string';
import { auth0Module } from '@/store/entityModules/auth0';
import { locale, t } from '@/i18n';

const baseUrl = process.env.VUE_APP_API_URL;

const client = axios.create();
const xApiKey = process.env.VUE_APP_X_API_KEY;

const buildHeaders = (token?: string): { [key: string]: string } => {
  const headers: { [key: string]: string } = {
    'Content-Type': 'application/json',
    'X-API-KEY': xApiKey ? xApiKey : '',
    'Accept-Language': locale,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

interface Response {
  status: number;
  config: {
    url: string;
  };
  data: {
    [key: string]: string;
  };
}

interface ErrorResponse {
  status: number;
  config: {
    url: string;
  };
  data: {
    data: {
      message: string;
    };
    status: string;
  };
}


export enum ErrorType {
  UNAUTHORIZED = 'unauthorized',
  FIRSTLOGIN = 'firstLogin',
  NO_VERIFIED = 'no_verified',
  NO_TOKEN = 'no_token',
}

client.interceptors.response.use(
  async (response) => {
    try {
      const r: Response = response as Response;
      // return response
      if (r.config.url) {
        if (r.status !== 200) {
          return Promise.reject(new Error(`HTTP Error: ${r.status}`));
        }
        if (r.data.error) {
          if (r.data.error === 'E003') {
            throw ErrorType.FIRSTLOGIN;
          } else if (r.data.error === 'E180') {
            Swal.fire('', t('error.emailNotVerified'), 'warning');
            throw ErrorType.NO_VERIFIED;
          } else {
            // Swal.fire(r.data.error, r.data.message, 'warning');
            const errorCode = String(r.data.error);
            const message = getErrorMessage(errorCode);
            Swal.fire({
              // if errorCode is an object, stringify it, otherwise display as is
              title: (typeof errorCode === 'object' && errorCode !== null) 
                ? JSON.stringify(errorCode) 
                : String(errorCode || 'Error'), 

              // same for message
              text: (typeof message === 'object' && message !== null) 
                ? JSON.stringify(message) 
                : (message ? String(message) : t('error.noDetailedErrorMessage')),

              icon: 'warning',
              customClass: {
                popup: 'swal-toast-front',
                container: 'swal-container-custom',
              },
              didOpen: () => {
                // adjust z-index of Swal container
                const swalContainer = document.querySelector('.swal2-container') as HTMLElement;

                if (swalContainer) {
                  swalContainer.style.zIndex = '99999';
                }
              }
            });
            return Promise.reject(response);
          }
        }
        return response;
      } else {
        if (r.data.status !== '0') { // when status != 0, there is some error
          throw Promise.reject(response);
        }
        return response;
      }
    } catch (error: any) {
      // do not throw as object
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }
  },
  async (error) => {
    try {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        if (axiosError.response.status === 500) {
          const status = String(axiosError.response.status);
          Swal.fire(status, t('error.emailNotAvailable'), 'error');
          throw Promise.reject(error);
        }
        if (axiosError.response.status >= 500) { // when auth error, redirect to signin screen
          const status = String(axiosError.response.status);
          // const message = String(axiosError.response.data.message);
          Swal.fire(status, t('error.internalServerError'), 'error');
          throw Promise.reject(error);
        }
        if (axiosError.response.status === 401) {
          if (process.env) {
            // window.location.href = '/logout';
            throw Promise.reject(ErrorType.UNAUTHORIZED);
          }
        }
        if (axiosError.response.status === 400) { // when auth error, redirect to signin screen
          const status = String(axiosError.response.status);
          const message = getErrorMessage(axiosError.response.data.error);
          Swal.fire(status, message, 'error');
          throw Promise.reject(error);
        }
      }
    } catch (error) {
      throw Promise.reject(error);
    }
  },
);

const getErrorMessage = (errorCode): string => {
  return when(errorCode)
    .on((v) => v === 'E000', () => t('error.default'))
    .on((v) => v === 'E001', () => t('error.invalidParams'))
    .on((v) => v === 'E002', () => t('error.tokenExpired'))
    .on((v) => v === 'E003', () => t('error.userNotFound'))
    .on((v) => v === 'E004', () => t('error.userAlreadyExists'))
    .on((v) => v === 'E005', () => t('error.invalidObjectType'))
    .on((v) => v === 'E006', () => t('error.breakfastNotFound'))
    .on((v) => v === 'E007', () => t('error.breakfastAlreadyExists'))
    .on((v) => v === 'E008', () => t('error.dinnerNotFound'))
    .on((v) => v === 'E009', () => t('error.dinnerAlreadyExists'))
    .on((v) => v === 'E010', () => t('error.bowelMovementNotFound'))
    .on((v) => v === 'E011', () => t('error.milkNotFound'))
    .on((v) => v === 'E012', () => t('error.sleepNotFound'))
    .on((v) => v === 'E013', () => t('error.temperatureNotFound'))
    .on((v) => v === 'E014', () => t('error.childcareJournalNotFound'))
    .on((v) => v === 'E015', () => t('error.childcareJournalAlreadyExists'))
    .on((v) => v === 'E016', () => t('error.eatingRecordNotFound'))
    .on((v) => v === 'E017', () => t('error.eatingRecordAlreadyExists'))
    .on((v) => v === 'E018', () => t('error.absenceOverlap'))
    .on((v) => v === 'E019', () => t('error.invalidReason'))
    .on((v) => v === 'E020', () => t('error.invalidPickedUpPerson'))
    .on((v) => v === 'E021', () => t('error.messageNotFound'))
    .on((v) => v === 'E022', () => t('error.pickupDataNotFound'))
    .on((v) => v === 'E023', () => t('error.classNotFound'))
    .on((v) => v === 'E024', () => t('error.childNotFound'))
    .on((v) => v === 'E025', () => t('error.childAlreadyRegistered'))
    .on((v) => v === 'E026', () => t('error.familyNotFound'))
    .on((v) => v === 'E027', () => t('error.userNotInFamily'))
    .on((v) => v === 'E028', () => t('error.userAlreadyInvited'))
    .on((v) => v === 'E029', () => t('error.formatNotFound'))
    .on((v) => v === 'E030', () => t('error.childIdNotSpecified'))
    .on((v) => v === 'E031', () => t('error.feedInfoNotFound'))
    .on((v) => v === 'E032', () => t('error.feedDetailNotFound'))
    .on((v) => v === 'E033', () => t('error.reportNotOwned'))
    .on((v) => v === 'E034', () => t('error.arrivalDepartureAlreadyExists'))
    .on((v) => v === 'E035', () => t('error.arrivalDepartureNotFound'))
    .on((v) => v === 'E036', () => t('error.arrivalRecordNotFound'))
    .on((v) => v === 'E037', () => t('error.arrivalRecordAlreadyExists'))
    .on((v) => v === 'E038', () => t('error.departureRecordAlreadyExists'))
    .on((v) => v === 'E039', () => t('error.arrivalDepartureNotOwned'))
    .on((v) => v === 'E040', () => t('error.invalidNursery'))
    .on((v) => v === 'E041', () => t('error.hiraganaCheckFailed'))
    .on((v) => v === 'E042', () => t('error.invalidInternalId'))
    .on((v) => v === 'E043', () => t('error.invalidUqid'))
    .on((v) => v === 'E044', () => t('error.invalidChecksum'))
    .on((v) => v === 'E045', () => t('error.userNotInNursery'))
    .on((v) => v === 'E046', () => t('error.adminOnly'))
    .on((v) => v === 'E047', () => t('error.invalidApprovalStatus'))
    .on((v) => v === 'E048', () => t('error.nurseryDiaryNotFound'))
    .on((v) => v === 'E049', () => t('error.menuNotOwned'))
    .on((v) => v === 'E050', () => t('error.userNotInNursery2'))
    .on((v) => v === 'E051', () => t('error.adminOnly2'))
    .on((v) => v === 'E052', () => t('error.invalidApprovalStatus2'))
    .on((v) => v === 'E053', () => t('error.nurseryDiaryNotFound2'))
    .on((v) => v === 'E054', () => t('error.noApprovalPermission'))
    .on((v) => v === 'E055', () => t('error.applicantMismatch'))
    .on((v) => v === 'E056', () => t('error.journalNotPending'))
    .on((v) => v === 'E057', () => t('error.alreadyApproved'))
    .on((v) => v === 'E058', () => t('error.initialJournalStatus'))
    .on((v) => v === 'E059', () => t('error.photoRecordNotFound'))
    .on((v) => v === 'E060', () => t('error.userNotInNursery3'))
    .on((v) => v === 'E061', () => t('error.foodInspectionAlreadyExists'))
    .on((v) => v === 'E062', () => t('error.foodInspectionNotFound'))
    .on((v) => v === 'E063', () => t('error.annualEventNotFound'))
    .on((v) => v === 'E064', () => t('error.classIdNotOwned'))
    .on((v) => v === 'E065', () => t('error.invalidSocketId'))
    .on((v) => v === 'E066', () => t('error.napCheckPatternNotFound'))
    .on((v) => v === 'E067', () => t('error.duplicateStaff'))
    .on((v) => v === 'E068', () => t('error.staffNotInNursery'))
    .on((v) => v === 'E069', () => t('error.napPatternNotOwned'))
    .on((v) => v === 'E070', () => t('error.childNotInNursery'))
    .on((v) => v === 'E071', () => t('error.napCheckNotFound'))
    .on((v) => v === 'E072', () => t('error.napCheckNotOwned'))
    .on((v) => v === 'E073', () => t('error.napCheckTempHumidityNotFound'))
    .on((v) => v === 'E074', () => t('error.napCheckNotPending'))
    .on((v) => v === 'E075', () => t('error.childAlreadyInNapCheck'))
    .on((v) => v === 'E076', () => t('error.childNotInNapCheck'))
    .on((v) => v === 'E077', () => t('error.staffAlreadyInNapCheck'))
    .on((v) => v === 'E078', () => t('error.napCheckCannotEdit'))
    .on((v) => v === 'E079', () => t('error.napCheckDetailNotFound'))
    .on((v) => v === 'E080', () => t('error.napCheckDetailNotOwned'))
    .on((v) => v === 'E081', () => t('error.napCheckDetailAlreadyExists'))
    .on((v) => v === 'E082', () => t('error.childNotInNapCheck2'))
    .on((v) => v === 'E083', () => t('error.invalidEventTime'))
    .on((v) => v === 'E084', () => t('error.noRelation'))
    .on((v) => v === 'E085', () => t('error.napSensorNotFound'))
    .on((v) => v === 'E086', () => t('error.commentLimit'))
    .on((v) => v === 'E087', () => t('error.absenceConfirmationFailed'))
    .on((v) => v === 'E088', () => t('error.pickupChangeConfirmationFailed'))
    .on((v) => v === 'E089', () => t('error.invalidEventPeriod'))
    .on((v) => v === 'E090', () => t('error.guardianNotFound'))
    .on((v) => v === 'E091', () => t('error.duplicateAllergyFood'))
    .on((v) => v === 'E092', () => t('error.allergyFoodNotOwned'))
    .on((v) => v === 'E093', () => t('error.staffAccountTitleExists'))
    .on((v) => v === 'E094', () => t('error.staffAccountTitleNotFound'))
    .on((v) => v === 'E095', () => t('error.staffAccountInfoExists'))
    .on((v) => v === 'E096', () => t('error.staffAccountInfoNotFound'))
    .on((v) => v === 'E097', () => t('error.staffTitleInUse'))
    .on((v) => v === 'E098', () => t('error.endDateBeforeStartDate'))
    .on((v) => v === 'E099', () => t('error.pastDateTime'))
    .on((v) => v === 'E100', () => t('error.excelGenerationFailed'))
    .on((v) => v === 'E101', () => t('error.napSensorNotStarted'))
    .on((v) => v === 'E102', () => t('error.napSensorRecordNotFound'))
    .on((v) => v === 'E103', () => t('error.absenceRegistrationNotFound'))
    .on((v) => v === 'E104', () => t('error.chatReadOnly'))
    .on((v) => v === 'E105', () => t('error.emailInUse'))
    .on((v) => v === 'E106', () => t('error.attachmentNotFound'))
    .on((v) => v === 'E107', () => t('error.certificationNotFound'))
    .on((v) => v === 'E108', () => t('error.certificationEndDateRequired'))
    .on((v) => v === 'E109', () => t('error.certificationPeriodOverlap'))
    .on((v) => v === 'E110', () => t('error.accountTitleNotFound'))
    .on((v) => v === 'E111', () => t('error.accountTitleInUse'))
    .on((v) => v === 'E112', () => t('error.pettyCashDetailNotFound'))
    .on((v) => v === 'E113', () => t('error.duplicateAllergyItem'))
    .on((v) => v === 'E114', () => t('error.allergyItemNotFound'))
    .on((v) => v === 'E115', () => t('error.allergyItemCannotModify'))
    .on((v) => v === 'E116', () => t('error.menuTypeNotFound'))
    .on((v) => v === 'E117', () => t('error.menuNotFound'))
    .on((v) => v === 'E118', () => t('error.foodInspectionNotFound2'))
    .on((v) => v === 'E119', () => t('error.foodInspectionAlreadyCreated'))
    .on((v) => v === 'E120', () => t('error.extendedCarePatternNotFound'))
    .on((v) => v === 'E121', () => t('error.timeSlotOverlap'))
    .on((v) => v === 'E122', () => t('error.timeSlotNotFound'))
    .on((v) => v === 'E123', () => t('error.timeStampSettingNotFound'))
    .on((v) => v === 'E124', () => t('error.extendedCareDiaryNotOwned'))
    .on((v) => v === 'E125', () => t('error.nurseryServiceNotRegistered'))
    .on((v) => v === 'E126', () => t('error.nurseryServiceAlreadyRegistered'))
    .on((v) => v === 'E127', () => t('error.nurseryServiceNotEnrolled'))
    .on((v) => v === 'E128', () => t('error.monthlyServiceSetDate'))
    .on((v) => v === 'E129', () => t('error.nurseryServiceInUse'))
    .on((v) => v === 'E130', () => t('error.invalidServiceType'))
    .on((v) => v === 'E131', () => t('error.invalidPaymentMonthType'))
    .on((v) => v === 'E132', () => t('error.invalidAccountType'))
    .on((v) => v === 'E133', () => t('error.userNotInManagementCompany'))
    .on((v) => v === 'E134', () => t('error.managementCompanyNotFound'))
    .on((v) => v === 'E135', () => t('error.invoiceNotFound'))
    .on((v) => v === 'E136', () => t('error.invalidInvoiceStatus'))
    .on((v) => v === 'E137', () => t('error.invoiceNotConfirmed'))
    .on((v) => v === 'E138', () => t('error.fileReadFailed'))
    .on((v) => v === 'E139', () => t('error.invalidChildCode'))
    .on((v) => v === 'E140', () => t('error.invalidReceivableValue'))
    .on((v) => v === 'E141', () => t('error.invalidReceivableValue'))
    .on((v) => v === 'E142', () => t('error.receivableImportNotFound'))
    .on((v) => v === 'E143', () => t('error.salesInfoSentCannotEdit'))
    .on((v) => v === 'E144', () => t('error.pettyCashConfirmedCannotChange'))
    .on((v) => v === 'E145', () => t('error.salesConfirmedCannotEdit'))
    .on((v) => v === 'E146', () => t('error.otherServiceDetailNotFound'))
    .on((v) => v === 'E147', () => t('error.childWithoutCertification'))
    .on((v) => v === 'E148', () => t('error.managementCompanyServiceNotFound'))
    .on((v) => v === 'E149', () => t('error.insufficientPermission'))
    .on((v) => v === 'E150', () => t('error.notDraftStatus'))
    .on((v) => v === 'E151', () => t('error.salesConfirmedCannotEdit2'))
    .on((v) => v === 'E152', () => t('error.nurseryAccountNotFound'))
    .on((v) => v === 'E153', () => t('error.childAlreadyWithdrawn'))
    .on((v) => v === 'E154', () => t('error.discussionNotFound'))
    .on((v) => v === 'E155', () => t('error.invalidGmoCardToken'))
    .on((v) => v === 'E156', () => t('error.cardRegistrationFailed'))
    .on((v) => v === 'E157', () => t('error.gmoCardNotFound'))
    .on((v) => v === 'E158', () => t('error.gmoCardDeleteFailed'))
    .on((v) => v === 'E243', () => t('error.fileTooLarge'))
    .otherwise(() => t('error.default'));
};

// REST
export default class ApiClient {
  public static async get(
    endpoint: string,
    params: { [key: string]: any } | null,
    isAuth = true,
  ) {
    const token = await auth0Module.getAuth0Token();
    if (isAuth && !token) {
      throw ErrorType.NO_TOKEN;
    }
    const headers = buildHeaders(isAuth ? token : undefined);
    if (params) {
      try {
        const response = await client.get(
          `${baseUrl}${endpoint}?${querystring.stringify(params, { skipNull: true })}`, {
          headers,
          data: {},
        });
        return response.data;
      } catch (error) {
        return  error;
      }
    } else {
      try {
        const response = await client.get(
          `${baseUrl}${endpoint}`, {
          headers,
          data: {},
        });
        return response.data;
      } catch (error) {
        return error;
      }
    }
  }

  public static async post(
    endpoint: string,
    params: { [key: string]: any },
    isAuth = true,
  ) {
    const token = await auth0Module.getAuth0Token();
    if (isAuth && !token) {
      throw ErrorType.NO_TOKEN;
    }
    const headers = buildHeaders(isAuth ? token : undefined);
    return client.post(
      `${baseUrl}${endpoint}`,
      JSON.stringify(params),
      { headers },
    ).then((response) => response.data);
  }

  public static async put(
    endpoint: string,
    params: { [key: string]: any } | null,
  ) {
    const token = await auth0Module.getAuth0Token();
    if (!token) {
      throw ErrorType.NO_TOKEN;
    }
    const headers = buildHeaders(token);
    if (params) {
      return client.put(
        `${baseUrl}${endpoint}`,
        JSON.stringify(params),
        { headers },
      ).then((response) => response.data);
    } else {
      return client.put(
        `${baseUrl}${endpoint}`,
        { headers },
      ).then((response) => response.data);
    }
  }

  public static async delete(
    endpoint: string,
    params: { [key: string]: any } | null,
  ) {
    const token = await auth0Module.getAuth0Token();
    if (!token) {
      throw ErrorType.NO_TOKEN;
    }
    const headers = buildHeaders(token);
    if (params) {
      return client.delete(
        `${baseUrl}${endpoint}?${querystring.stringify(params, { skipNull: true })}`, {
        headers,
        data: {},
      },
      ).then((response) => response.data);
    } else {
      return client.delete(
        `${baseUrl}${endpoint}`,
        {
          headers,
          data: {},
        },
      ).then((response) => response.data);
    }
  }

}
